/** Field-aware comparison for the reconciliation UI only.
 * Canonical sync equality, stored values and write authority remain unchanged.
 */
import type { ExtractedClaim } from '../../types/siteContext';
import {
  buildSedContextDiffRows, effectiveDiffStatus, isOpenDivergence,
  type SedContextDiffRow, type EffectiveDiffStatus,
} from './syncSedContextBindings';

export interface PresentedSedContextDiffRow extends SedContextDiffRow {
  comparison: 'canonical' | 'rounded' | 'not-comparable';
  comparisonNote?: string;
}
export type PresentedDiffStatus = EffectiveDiffStatus | 'rounded' | 'not-comparable';

function numberToken(value: number, span: string, unit: RegExp): string | undefined {
  const numbers = /(-?\d+(?:\.\d+)?)\s*([a-z]+)\b/gi;
  for (const match of span.matchAll(numbers)) {
    if (Number(match[1]) === value && unit.test(match[2]!)) return match[1];
  }
  return undefined;
}

/** Preserve the original row and claim; incompatible dimensions are reviewable,
 * never treated as a physical disagreement or an automatically adoptable value. */
export function presentSedContextDiffRows(
  rows: readonly SedContextDiffRow[], claims: readonly ExtractedClaim[],
): PresentedSedContextDiffRow[] {
  const byId = new Map(claims.map(c => [c.id, c]));
  return rows.map(row => {
    const base: PresentedSedContextDiffRow = { ...row, comparison: 'canonical' };
    const claim = row.claimId ? byId.get(row.claimId) : undefined;
    if (!claim || !['parcel.acres', 'grid.substationDistKm', 'environmental.hasWetlands'].includes(row.sedPath)) return base;
    const refuse = (comparisonNote: string): PresentedSedContextDiffRow => ({ ...base, comparison: 'not-comparable', comparisonNote });
    if (claim.sedField && claim.sedField !== row.sedPath) {
      return refuse(`Context refers to ${claim.sedField}, not ${row.sedPath}. Review the field binding before adopting either value.`);
    }
    const value = claim.claimedValue;
    if (value == null) return base;
    if (row.sedPath === 'environmental.hasWetlands') {
      return typeof value === 'boolean' ? base : refuse('Wetlands presence is Yes/No. An area or other value cannot be compared with presence.');
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) return refuse('This field requires a finite numeric value in its stated unit.');
    const span = claim.sourceSpan;
    if (row.sedPath === 'grid.substationDistKm') {
      const token = numberToken(value, span, /^(?:km|kilometers?|kilometres?)$/i);
      if ((!claim.sedField && claim.source === 'document-extracted' && !/\bsubstation\b/i.test(span)) || (!token && claim.source === 'document-extracted')) {
        return refuse('Context does not identify a substation distance in km. A power or capacity value cannot be used as distance.');
      }
      if (/\b(?:MW|GW|kW|miles?|mi)\b/i.test(span) && !token) return refuse('The stated unit is incompatible with substation distance in km.');
      return base;
    }
    if (/\b(?:net|usable|buildable|developable)\b/i.test(span)) return refuse('Usable or buildable area is a different field from gross parcel acreage.');
    const token = numberToken(value, span, /^(?:ac|acres?)$/i);
    if (!token) {
      if (claim.source === 'document-extracted' || /\b(?:ha|hectares?|km|MW)\b/i.test(span)) return refuse('Context does not state this parcel value in acres. Review the field and unit.');
      return base; // Older typed manual claims retain their existing comparison.
    }
    if (row.status !== 'diverged' || typeof row.sedValue !== 'number' || !Number.isFinite(row.sedValue)) return base;
    const decimals = token.includes('.') ? token.split('.')[1]!.length : 0;
    if (decimals > 6) return base;
    const factor = 10 ** decimals;
    if (Math.round(row.sedValue * factor) === Math.round(value * factor)) {
      return { ...base, comparison: 'rounded', comparisonNote: `Same acreage at the source's stated ${decimals}-decimal precision. The stored values remain distinct.` };
    }
    return base;
  });
}

export function buildPresentedSedContextDiffRows(args: Parameters<typeof buildSedContextDiffRows>[0]): PresentedSedContextDiffRow[] {
  return presentSedContextDiffRows(buildSedContextDiffRows(args), args.siteContext?.claims ?? []);
}
export function presentedDiffStatus(row: PresentedSedContextDiffRow): PresentedDiffStatus {
  return row.comparison === 'canonical' ? effectiveDiffStatus(row) : row.comparison;
}
export function isPresentedOpenDivergence(row: PresentedSedContextDiffRow): boolean {
  return row.comparison === 'canonical' && isOpenDivergence(row);
}
